import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IdentifierAutoComplete } from "./IdentifierAutoComplete";
import { getDocument } from "pdfjs-dist";
import Mammoth from "mammoth";
import { useState } from "react";
import { Info } from "@mui/icons-material";
import { FileCard } from "../../../shared-components/FileCard";

const extractTextFromPdf = async (setExtractedText, file) => {
  const reader = new FileReader();
  reader.onload = async () => {
    try {
      const pdf = await getDocument({ data: reader.result }).promise;
      let extractedText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        extractedText +=
          textContent.items.map((item) => item.str).join(" ") + "\n";
      }
      setExtractedText(extractedText);
    } catch (error) {
      console.error("Error extracting text from PDF:", error);
    }
  };
  reader.readAsArrayBuffer(file);
};

function extractFromWordDoc(setExtractedText, file) {
  const reader = new FileReader();
  reader.onload = async (event) => {
    const arrayBuffer = event.target.result;
    try {
      const result = await Mammoth.extractRawText({ arrayBuffer });
      setExtractedText(result.value);
    } catch (error) {
      console.error("Error extracting text:", error);
    }
  };
  reader.readAsArrayBuffer(file);
}

export const AnswerFileUploadDialog = ({
  open,
  setOpen,
  file,
  identifier,
  setIdentifier,
  identifiers,
  onSubmitProcessedAnswer,
  onSubmitHandwrittenAnswer,
  studentView = false,
}) => {
  const [fileType, setFileType] = useState("Processed");
  const [extractedText, setExtractedText] = useState("");
  const handleNext = () => {
    const wordExtensions = [".doc", ".docx"];

    if (fileType === "Processed" && !extractedText) {
      if (file.name.toLowerCase().endsWith(".pdf")) {
        extractTextFromPdf(setExtractedText, file);
      } else if (
        wordExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
      ) {
        extractFromWordDoc(setExtractedText, file);
      } else {
        throw Error("Unknown file type");
      }
    } else if (extractedText) {
      onSubmitProcessedAnswer(extractedText, identifier);
      handleClose();
    } else {
      onSubmitHandwrittenAnswer(identifier);
      handleClose();
    }
  };

  const handleClose = () => {
    if (!studentView) {
      setIdentifier({});
    }
    setExtractedText("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Upload File</DialogTitle>
      <DialogContent>
        {!studentView && (
          <IdentifierAutoComplete
            availableIdentifiers={identifiers}
            setIdentifier={setIdentifier}
            identifier={identifier}
          />
        )}
        <Box sx={{ mt: 2 }}>
          <FileCard file={file} />
        </Box>

        {!extractedText ? (
          <FormControl sx={{ mt: 2 }}>
            <RadioGroup
              name="file-type-radio-buttons-group"
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
            >
              <FormControlLabel
                sx={{ mb: 2 }}
                value="Processed"
                control={<Radio />}
                label={
                  <Stack spacing={0.2}>
                    <Typography variant="body1">Processed</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Word processed document
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value="Handwritten"
                control={<Radio />}
                label={
                  <Stack spacing={0.2}>
                    <Typography variant="body1">Handwritten</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Scanned handwritten document
                    </Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
              <Info sx={{ color: "info.main" }} />
              <Typography variant={"body2"}>
                Review the extracted text below and confirm submission
              </Typography>
            </Stack>
            <FormControl fullWidth>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <TextField
                  multiline
                  minRows={6}
                  maxRows={6}
                  fullWidth
                  margin="normal"
                  value={extractedText}
                  onChange={(e) => setExtractedText(e.target.value)}
                />
              </Box>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={!identifier || Object.keys(identifier).length === 0}
            onClick={handleNext}
            variant="contained"
          >
            {extractedText ? "Submit" : "Next"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export { extractTextFromPdf, extractFromWordDoc };
